/**
 * Spray Record Models
 *
 * A spray record (mission) represents a single application event on one field.
 * Multiple drones can participate, but they must all use the same chemicals.
 * Each drone's work is tracked separately for billing and compliance.
 */

// ============================================
// DROPDOWN OPTIONS (match CropFlight aerial-record)
// ============================================

export const EQUIPMENT_MALFUNCTION_TYPES = [
  'Control Station',
  'Electrical System',
  'Fuel System',
  'Navigation System',
  'Onboard Flight Control System',
  'Power Plant',
  'Lost Link Events'
] as const;

export const ACCIDENT_TYPES = [
  'Fatal Injury',
  'Serious Injury - Hospitalization',
  'Serious Injury - Fracture of Bone',
  'Serious Injury - Severe Hemorrhage',
  'Serious Injury - Nerve Muscle or Tendon',
  'Serious Injury - Internal Organs',
  'Serious Injury - Burns',
  'Serious Injury - Other'
] as const;

export const UA_LOSS_TYPES = [
  'Substantial Damage to Aircraft',
  'Substantial Damage to Property'
] as const;

// ============================================
// APPLICATION RATE UNIT OPTIONS
// ============================================

export const RATE_UNIT_OPTIONS = [
  { value: 'oz/ac', label: 'oz/ac' },
  { value: 'pt/ac', label: 'pt/ac' },
  { value: 'qt/ac', label: 'qt/ac' },
  { value: 'gal/ac', label: 'gal/ac' },
  { value: 'lb/ac', label: 'lb/ac' },
] as const;

export const DEFAULT_RATE_UNIT = 'oz/ac';

/**
 * Parse a compound rate unit string like "oz/ac" into unit and perUnit parts
 */
export function parseRateUnit(rateUnit: string): { unit: string; perUnit: string } {
  const parts = rateUnit.split('/');
  return { unit: parts[0] || 'oz', perUnit: parts[1] || 'ac' };
}

/**
 * Build a display rate string from value and unit
 * e.g., (22, "oz/ac") -> "22 oz/ac"
 */
export function buildRateString(value: number, rateUnit: string): string {
  return `${value} ${rateUnit}`;
}

// ============================================
// UNIT CONVERSION (liquid volume)
// ============================================

/** Conversion factors: unit → fluid ounces */
const OZ_PER: Record<string, number> = {
  oz: 1,
  pt: 16,
  qt: 32,
  gal: 128,
};

/**
 * Convert a value from one liquid unit to another.
 * Returns null if either unit is unrecognized (e.g., lb).
 */
export function convertUnit(value: number, fromUnit: string, toUnit: string): number | null {
  const from = OZ_PER[fromUnit];
  const to = OZ_PER[toUnit];
  if (from == null || to == null) return null;
  return (value * from) / to;
}

/**
 * Convert a value in fluid ounces to the most human-friendly unit.
 *   >= 128 oz → gal
 *   >= 32 oz  → qt
 *   >= 16 oz  → pt
 *   else      → oz
 */
export function formatBestUnit(ozValue: number): { value: number; unit: string } {
  if (ozValue >= 128) return { value: ozValue / 128.0, unit: 'gal' };
  if (ozValue >= 32)  return { value: ozValue / 32.0, unit: 'qt' };
  if (ozValue >= 16)  return { value: ozValue / 16.0, unit: 'pt' };
  return { value: ozValue, unit: 'oz' };
}

/**
 * Normalize a value + unit to fluid ounces.
 * Returns null for unrecognized units (e.g., lb).
 */
export function toOz(value: number, unit: string): number | null {
  const factor = OZ_PER[unit];
  return factor != null ? value * factor : null;
}

// ============================================
// GROUND RECORD OPTIONS (match CropFlight ground-record)
// ============================================

export const PPE_OPTIONS = [
  { value: 'gloves', label: 'Gloves' },
  { value: 'coveralls', label: 'Coveralls' },
  { value: 'apron', label: 'Apron' },
  { value: 'faceShield', label: 'Face Shield' },
  { value: 'goggles', label: 'Goggles' },
  { value: 'respirator', label: 'Respirator' },
  { value: 'rubberBoots', label: 'Rubber Boots' }
] as const;

export const APPLICATION_TYPE_OPTIONS = [
  { value: 'herbicide', label: 'Herbicide' },
  { value: 'pesticide', label: 'Pesticide' },
  { value: 'fungicide', label: 'Fungicide' },
  { value: 'fertilizer', label: 'Fertilizer' },
  { value: 'other', label: 'Other' }
] as const;

// ============================================
// INCIDENT INFORMATION
// ============================================

/**
 * Incident information for FAA compliance reporting
 */
export interface IncidentInformation {
  takeOffOrLandingDamage: boolean;
  descriptionOfTakeOffOrLandingDamage?: string;
  equipmentMalfunction: boolean;
  equipmentMalfunctionType?: string;
  descriptionOfEquipmentMalfunction?: string;
  hasAccident: boolean;
  accidentType?: string;
  accidentDescription?: string;
  uaLossType?: string;
}

// ============================================
// SPRAY DETAILS
// ============================================

/**
 * Spray/treatment details for application records
 */
export interface SprayDetails {
  whatWasTreated?: string;
  targetPest?: string;
  nozzleDescription?: string;
  nozzleToTargetDistance?: string;
  pumpPressure?: string;
  travelSpeed?: string;
  descriptionOfDriftManagementPractices?: string;
}

// ============================================
// VISUAL OBSERVER INFO
// ============================================

/**
 * Visual observer and NOTAM information
 */
export interface VisualObserverInfo {
  visualObserver?: string;
  voTested: boolean;
  notam?: string;
}

// ============================================
// SAFETY & COMPLIANCE (match CropFlight ground-record)
// ============================================

/**
 * Safety and compliance information for state reporting
 */
export interface SafetyComplianceInfo {
  reentryInterval?: string;           // Duration before field re-entry is safe
  ppeRequired?: string[];             // Array of PPE values (gloves, coveralls, etc.)
  rinseEquipmentPostApplication?: string;  // Equipment rinsing procedures
}

/**
 * Emergency contact information
 */
export interface EmergencyContactInfo {
  emergencyContactNumber?: string;
  stateEmergencyContactNumber?: string;
}

/**
 * Application type tracking (herbicide, pesticide, etc.)
 */
export interface ApplicationTypeInfo {
  applicationTypes?: string[];        // Array of selected types
  applicationTypeOther?: string;      // Description if 'other' is selected
}

/**
 * Load/mix location for chemical mixing site
 */
export interface LoadMixLocation {
  useLocationForLoadMixLocation: boolean;  // If true, use spray location
  loadMixLocationLatitude?: number;
  loadMixLocationLongitude?: number;
}

/**
 * Chemical usage tracking per drone
 * Tracks how much of each chemical a specific drone used
 */
export interface ChemicalUsage {
  appliedChemicalId: string;  // Reference to the AppliedChemical
  chemicalName: string;       // Denormalized for display
  rate: string;               // Target rate (e.g., "22 oz/ac")
  rateValue?: number | null;  // Numeric portion of rate
  rateUnit?: string | null;   // Unit: oz, lb, gal, qt, pt
  ratePerUnit?: string | null; // Per-unit: ac, hac
  amountUsed: string;         // Calculated/actual amount (e.g., "2.75 gal")
  amountUsedValue?: number | null;  // Numeric portion of amountUsed
  amountUsedUnit?: string | null;   // Unit: oz, lb, gal, qt, pt
}

/**
 * Flight path data for a drone
 * Can be uploaded from KML/GPX or drawn on map
 */
export interface FlightPathData {
  id: string;
  name: string;
  source: 'kml' | 'gpx' | 'drawn';  // How the path was created
  fileName?: string;                 // Original file name if uploaded
  coordinates: { lat: number; lng: number }[];
  uploadedAt: string;
  areaAcres?: number;               // Calculated area from path
  altitude?: number | null;
  speed?: number | null;
  droneName?: string | null;
  droneId?: string | null;
  pilotName?: string | null;
  flightTime?: number | null;
  startTime?: string | null;
  endTime?: string | null;
}

/**
 * Individual drone flight entry within a spray record
 * Tracks one drone's contribution to the mission
 */
export interface DroneFlightEntry {
  id: string;

  // Equipment & personnel
  droneId: string;
  droneSource: 'adn' | 'organization';  // Which table the droneId references
  droneName?: string;       // Denormalized for display
  pilotId: string;
  pilotName?: string;       // Denormalized for display

  // Flight timing
  startTime: string;
  endTime: string;

  // Flight parameters
  flightAltitude: number;   // feet
  flightSpeed: number;      // mph

  // Work completed by this drone
  acreageSprayed: number;   // Actual acres this drone covered

  /**
   * Takeoffs and landings by THIS aircraft at THIS location (FAA §F.2.d).
   *
   * Null means "not determined" — a materially different fact from a real 0.
   * The FAA report floors null to 1 at render time only; the 1 is never
   * written back, so null keeps meaning "this still needs a real number".
   * Never test this with `if (numFlights)` — falsy-zero is how this feature
   * ships broken.
   */
  numFlights: number | null;

  // Chemical usage for this drone (calculated from acreage × rate)
  chemicalUsage: ChemicalUsage[];

  // Flight path for this specific drone (required for compliance)
  flightPath: FlightPathData | null;
}

/**
 * Chemical added to a spray record
 * Shared across all drones in the mission
 */
export interface AppliedChemical {
  id: string;
  chemicalId?: string;      // Reference to Chemical if from book/custom
  name: string;
  epaNumber: string;
  rate: string;             // Target application rate (e.g., "22 oz/acre")
  rateValue: number;        // Numeric value for calculations
  rateUnit: string;         // Unit (oz, gal, pt, qt, lb, etc.)
  ratePerUnit: string;      // "acre" typically
  source: 'book' | 'custom' | 'template';
  templateName?: string;    // If from a template, track which one
  category?: string;        // herbicide, insecticide, etc.
}

/**
 * Weather conditions at time of application
 */
export interface WeatherConditions {
  temperature: number;      // Fahrenheit
  humidity: number;         // Percentage
  windSpeed: number;        // mph
  windDirection: string;    // Cardinal direction (N, NE, etc.)
  conditions: string;       // Description (Clear, Cloudy, etc.)
}

/**
 * Complete spray record (mission)
 */
export interface SprayRecord {
  id: string;
  organizationId: string;

  // Application details
  applicationDate: string;  // Backend uses applicationDate
  status: 'draft' | 'pending' | 'completed' | 'cancelled';

  // Field (single per record)
  fieldId: string;
  fieldName?: string;       // Denormalized for display
  fieldAcreage?: number;    // Total field acreage for reference

  // Spray details
  sprayDetails?: SprayDetails;

  // Chemicals (shared across all drones - same mix required)
  chemicals: AppliedChemical[];
  totalVolume?: string;     // Total tank mix volume
  tankMixNotes?: string;

  // Operating certification (snapshot from when record was created)
  certificationId: string;
  certificationName?: string;
  certificationNumber?: string;
  certificationState?: string;
  certificationExpiration?: string;

  // Visual observer & NOTAM
  visualObserverInfo?: VisualObserverInfo;

  // Drone flights (one or more drones)
  droneFlights: DroneFlightEntry[];

  // Weather at application
  weather: WeatherConditions;

  // Incident information (optional)
  incidentInfo?: IncidentInformation;

  // Safety & Compliance (ground record fields)
  safetyCompliance?: SafetyComplianceInfo;

  // Emergency contacts
  emergencyContact?: EmergencyContactInfo;

  // Application types
  applicationTypeInfo?: ApplicationTypeInfo;

  // Load/Mix location
  loadMixLocation?: LoadMixLocation;

  // Compliance checklist
  preFlightChecklist: boolean;
  bufferZonesVerified: boolean;

  // Additional notes
  notes?: string;

  // Audit fields
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * Helper to parse rate string into value and unit
 * e.g., "22 oz/acre" -> { value: 22, unit: "oz", per: "ac" }
 */
export function parseRate(rateString: string): { value: number; unit: string; per: string } | null {
  const trimmedRate = rateString.trim();

  // Check for bare number (no unit)
  if (trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)$/)) {
    const { unit, perUnit } = parseRateUnit(DEFAULT_RATE_UNIT);
    return { value: parseFloat(trimmedRate), unit, per: perUnit };
  }

  // Unit "/" per-unit, e.g. "32 oz/acre", "15oz/ac", "22oz / acre", ".5 lb/ac"
  // Also allows a repeated "/ac" saved by an earlier bug, e.g. "23 gal/ac/ac" -> gal/ac
  const slashMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)\s*\/\s*(acre|ac)(?:\/ac)?$/i);
  if (slashMatch) {
    return { value: parseFloat(slashMatch[1]), unit: slashMatch[2].toLowerCase(), per: 'ac' };
  }

  // Unit with the word "per", e.g. "16 oz. Per acre" -> oz/ac
  const perWordMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)\.?\s+per\s+acre$/i);
  if (perWordMatch) {
    return { value: parseFloat(perWordMatch[1]), unit: perWordMatch[2].toLowerCase(), per: 'ac' };
  }

  // Unit with no per-unit, e.g. "22 oz", "11 gals" -> assume per acre ("gals" -> "gal")
  const unitOnlyMatch = trimmedRate.match(/^(\d+(?:\.\d+)?|\.\d+)\s*(oz|pt|qt|gal|lb)s?$/i);
  if (unitOnlyMatch) {
    return { value: parseFloat(unitOnlyMatch[1]), unit: unitOnlyMatch[2].toLowerCase(), per: 'ac' };
  }

  // Anything else is not a known format
  return null;
}

/**
 * Parse a rate string into a compound unit string for the dropdown
 * e.g., "22 oz/acre" -> { value: 22, rateUnit: "oz/ac" }
 */
export function parseRateToStructured(rateString: string): { value: number; rateUnit: string } | null {
  const parsed = parseRate(rateString);
  if (!parsed) return null;
  return {
    value: parsed.value,
    rateUnit: `${parsed.unit}/${parsed.per}`
  };
}

/**
 * Calculate chemical usage based on acreage and rate.
 * Multiplies rate × acreage in the rate's native unit, then
 * normalizes to the most readable liquid unit.
 */
export function calculateChemicalUsage(acreage: number, rateString: string): string {
  const parsed = parseRate(rateString);
  if (!parsed) return 'N/A';

  const totalInRateUnit = acreage * parsed.value;

  // Convert to oz, pick best display unit, convert back
  const totalOz = toOz(totalInRateUnit, parsed.unit);
  if (totalOz == null) {
    // Non-liquid unit (lb, etc.) — return as-is
    return `${totalInRateUnit.toFixed(2)} ${parsed.unit}`;
  }

  const best = formatBestUnit(totalOz);
  return `${best.value.toFixed(2)} ${best.unit}`;
}

/**
 * Calculate totals across all drone flights.
 * Normalizes all amounts to fluid ounces before summing,
 * then formats back to the most readable unit.
 */
export function calculateRecordTotals(droneFlights: DroneFlightEntry[]): {
  totalAcreage: number;
  totalChemicalUsage: Map<string, { name: string; totalAmount: string }>;
} {
  const totalAcreage = droneFlights.reduce((sum, df) => sum + df.acreageSprayed, 0);

  // Aggregate chemical usage across all drones (liquids in fl oz; for lb chemicals totalOz holds pounds)
  const chemicalTotals = new Map<string, { name: string; totalOz: number; isLb: boolean }>();

  droneFlights.forEach(df => {
    df.chemicalUsage.forEach(cu => {
      const parsed = parseRate(cu.amountUsed);
      if (parsed) {
        // lb is weight: add pounds as-is; liquids are converted to fl oz
        const oz = parsed.unit === 'lb' ? parsed.value : toOz(parsed.value, parsed.unit);
        if (oz == null) return; // skip unknown units
        const existing = chemicalTotals.get(cu.appliedChemicalId);
        if (existing) {
          existing.totalOz += oz;
        } else {
          chemicalTotals.set(cu.appliedChemicalId, {
            name: cu.chemicalName,
            totalOz: oz,
            isLb: parsed.unit === 'lb'
          });
        }
      }
    });
  });

  const totalChemicalUsage = new Map<string, { name: string; totalAmount: string }>();
  chemicalTotals.forEach((value, key) => {
    const best = formatBestUnit(value.totalOz);
    totalChemicalUsage.set(key, {
      name: value.name,
      totalAmount: value.isLb
        ? `${value.totalOz.toFixed(2)} lb`
        : `${best.value.toFixed(2)} ${best.unit}`
    });
  });

  return { totalAcreage, totalChemicalUsage };
}
